import { useCallback, useEffect, useState } from 'react'

import type { AvatarKey } from '@hazel/shared'

import { api, ApiError } from '../api/client.js'
import { useSession, type SessionUser } from '../auth/session-context.js'
import { Avatar } from '../components/Avatar.js'
import { Button } from '../components/Button.js'
import { Card } from '../components/Card.js'
import { Input } from '../components/Input.js'
import { TeddyChoice } from '../components/TeddyChoice.js'
import { ResetPassword } from './ResetPassword.js'

/** Parent-only screen for adding accounts and picking a teddy for each. */
export function Accounts() {
  // Who is doing the resetting, so their own account can say the true thing
  // about what happens: the server hands *them* a fresh session rather than
  // signing them out mid-sentence.
  const { user } = useSession()
  const [accounts, setAccounts] = useState<SessionUser[] | null>(null)
  const [username, setUsername] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [avatarKey, setAvatarKey] = useState<AvatarKey>('hoppity')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  /**
   * The list, and what to say when it does not arrive.
   *
   * The failure used to go nowhere: `void load()` discards the promise, so a
   * refused or unreachable request became an unhandled rejection and the page
   * showed an empty list — indistinguishable from a household with no accounts
   * in it, which is the one thing this page exists to tell you about.
   *
   * It also failed the test suite from outside any test. The rejection surfaces
   * after whichever test mounted this page has finished, so Vitest attributes
   * it to whatever was running at the time and fails a run in which every test
   * passed. That is the least legible red there is, and it blocked a release
   * (DOL-98).
   *
   * Adding an account already reports its failure this way; only the load did
   * not.
   */
  const load = useCallback(async () => {
    try {
      setAccounts(await api<SessionUser[]>('/api/users'))
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Could not load the accounts just now.',
      )
    }
  }, [])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load()
  }, [load])

  function addAccount(event: React.FormEvent): void {
    event.preventDefault()
    void submit()
  }

  async function submit(): Promise<void> {
    setError(null)
    setSaving(true)

    try {
      await api('/api/users', {
        method: 'POST',
        body: JSON.stringify({
          username,
          displayName: displayName || username,
          password,
          avatarKey,
        }),
      })

      setUsername('')
      setDisplayName('')
      setPassword('')
      await load()
    } catch (caught: unknown) {
      setError(caught instanceof ApiError ? caught.message : 'Could not add that account.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-(length:--text-hero)">Accounts</h1>

      <Card>
        <h2 className="text-(length:--text-heading) mb-3">Who plays</h2>

        {accounts === null ? (
          <p role="status" className="text-ink-muted">
            Loading…
          </p>
        ) : (
          <ul className="flex list-none flex-col gap-3 p-0">
            {accounts.map((account) => (
              <li
                key={account.id}
                className="border-border flex flex-col gap-2 border-b pb-3 last:border-0 last:pb-0"
              >
                <div className="flex items-center gap-3">
                  <Avatar teddy={account.avatarKey} name={account.displayName} size="small" />
                  <span className="font-display">{account.displayName}</span>
                  <span className="text-ink-muted text-(length:--text-small)">
                    {account.username} · {account.role}
                  </span>
                </div>

                {/*
                  A forgotten password had no answer inside the app at all, and
                  this is the screen that already knows who everybody is.
                */}
                <ResetPassword account={account} isSelf={account.id === user?.id} />
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <h2 className="text-(length:--text-heading) mb-3">Add someone</h2>

        <form onSubmit={addAccount} className="flex flex-col gap-4">
          <Input
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            hint="What they type to log in. Letters and numbers."
            autoComplete="off"
            required
          />
          <Input
            label="Name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            hint="What the app calls them. Defaults to the username."
            autoComplete="off"
          />
          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            hint="At least 8 characters."
            autoComplete="new-password"
            required
          />

          <TeddyChoice
            chosen={avatarKey}
            onChoose={setAvatarKey}
            legend="Pick a picture"
            name="avatar"
          />

          {error ? (
            <p role="alert" className="text-coral">
              {error}
            </p>
          ) : null}

          <Button type="submit" disabled={saving}>
            {saving ? 'Adding…' : 'Add account'}
          </Button>
        </form>
      </Card>
    </div>
  )
}
